import React from 'react'
import { AlertTriangle } from 'react-feather'

interface StatusBadgeProps {
  status: string
  type?: 'status' | 'range' | 'facility' | 'insurance' | 'network' | 'claim_status'
  lastUpdateDate?: string | null
}

// Calculate days since last update for stale indicator
const getDaysSinceUpdate = (dateString: string | null | undefined): number | null => {
  if (!dateString) return null
  const lastUpdate = new Date(dateString)
  const now = new Date()
  const diffTime = now.getTime() - lastUpdate.getTime()
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
  return diffDays
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status, type = 'status', lastUpdateDate }) => {
  const getStatusStyles = () => {
    const statusLower = status?.toLowerCase() || ''

    if (type === 'status') {
      // Match PatientDetailPanel colors
      if (statusLower.includes('approved') || statusLower.includes('booked') || statusLower === 'arrived') {
        return 'bg-green-500/20 text-green-400 border-green-500/30'
      }
      if (statusLower.includes('yellow stripe')) {
        return 'bg-yellow-500/30 text-yellow-300 border-yellow-400/50 shadow-sm shadow-yellow-500/20'
      }
      if (statusLower.includes('pending') || statusLower.includes('prospect') || statusLower.includes('waiting')) {
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30'
      }
      if (statusLower.includes('denied') || statusLower === 'mia' || statusLower.includes('clinically denied')) {
        return 'bg-red-500/20 text-red-400 border-red-500/30'
      }
      if (statusLower.includes('private pay')) {
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30'
      }
      if (statusLower === 'outsource/follow up') {
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30'
      }
      if (statusLower === 'ref to partner') {
        return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30'
      }
      if (statusLower.includes('cancelled') || statusLower.includes('inactive')) {
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30'
      }
      if (statusLower.includes('active') || statusLower.includes('confirmed')) {
        return 'bg-green-500/20 text-green-400 border-green-500/30'
      }
      if (statusLower.includes('new')) {
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30'
      }
      // Roles / Privileges
      if (statusLower === 'owner') {
        return 'bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-400 border-amber-500/30'
      }
      if (statusLower === 'admin') {
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30'
      }
      if (statusLower === 'dev') {
        return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
      }
      if (statusLower === 'director') {
        return 'bg-rose-500/20 text-rose-400 border-rose-500/30'
      }
      if (statusLower === 'manager') {
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30'
      }
      if (statusLower === 'staff') {
        return 'bg-slate-500/20 text-slate-400 border-slate-500/30'
      }
      return 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    }

    if (type === 'facility') {
      // Facilities
      if (statusLower.includes('axis')) {
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30'
      }
      if (statusLower.includes('beachside')) {
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30'
      }
      // Departments (also use facility type for styling)
      if (statusLower === 'intake') {
        return 'bg-teal-500/20 text-teal-400 border-teal-500/30'
      }
      if (statusLower === 'administration') {
        return 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30'
      }
      if (statusLower === 'claims') {
        return 'bg-amber-500/20 text-amber-400 border-amber-500/30'
      }
      if (statusLower === 'followup') {
        return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30'
      }
      if (statusLower === 'bd') {
        return 'bg-pink-500/20 text-pink-400 border-pink-500/30'
      }
      if (statusLower === 'dev') {
        return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
      }
      return 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    }

    if (type === 'network') {
      if (statusLower.includes('in-network') && !statusLower.includes('out')) {
        return 'bg-green-500/20 text-green-400 border-green-500/30'
      }
      if (statusLower.includes('out-of-network')) {
        return 'bg-red-500/20 text-red-400 border-red-500/30'
      }
      return 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    }

    if (type === 'claim_status') {
      if (statusLower === 'paid') {
        return 'bg-green-500/20 text-green-400 border-green-500/30'
      }
      if (statusLower === 'partial paid') {
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
      }
      if (statusLower.includes('balance due')) {
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30'
      }
      if (statusLower.includes('claim at insurance')) {
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30'
      }
      if (statusLower.includes('write off')) {
        return 'bg-red-500/20 text-red-400 border-red-500/30'
      }
      if (statusLower === 'pending') {
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
      }
      return 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    }

    if (type === 'range') {
      return 'bg-accent-purple/20 text-accent-purple border-accent-purple/30'
    }

    if (type === 'insurance') {
      if (statusLower.includes('aetna')) {
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30'
      }
      if (statusLower.includes('bcbs') || statusLower.includes('blue cross')) {
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30'
      }
      if (statusLower.includes('cigna')) {
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30'
      }
      if (statusLower.includes('united') || statusLower.includes('uhc')) {
        return 'bg-sky-500/20 text-sky-400 border-sky-500/30'
      }
      if (statusLower.includes('humana')) {
        return 'bg-green-500/20 text-green-400 border-green-500/30'
      }
      if (statusLower.includes('kaiser')) {
        return 'bg-red-500/20 text-red-400 border-red-500/30'
      }
      if (statusLower.includes('anthem')) {
        return 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30'
      }
      if (statusLower.includes('medicare')) {
        return 'bg-teal-500/20 text-teal-400 border-teal-500/30'
      }
      if (statusLower.includes('medicaid')) {
        return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
      }
      if (statusLower.includes('molina')) {
        return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30'
      }
      if (statusLower.includes('oscar')) {
        return 'bg-pink-500/20 text-pink-400 border-pink-500/30'
      }
      if (statusLower.includes('ambetter')) {
        return 'bg-amber-500/20 text-amber-400 border-amber-500/30'
      }
      return 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    }

    return 'bg-gray-500/20 text-gray-400 border-gray-500/30'
  }

  if (!status || status === 'N/A' || status === '') {
    return <span className="text-gray-500 text-sm">-</span>
  }

  // Show stale indicator for Yellow Stripe leads
  const isYellowStripe = status?.toLowerCase().includes('yellow stripe')
  const daysSinceUpdate = isYellowStripe ? getDaysSinceUpdate(lastUpdateDate) : null

  // Determine urgency level based on days stale
  const getUrgencyStyles = (days: number | null) => {
    if (days === null) return ''
    if (days >= 7) return 'ring-2 ring-red-500 ring-offset-1 ring-offset-dark-bg'
    if (days >= 4) return 'ring-2 ring-orange-500 ring-offset-1 ring-offset-dark-bg'
    return ''
  }

  return (
    <div className="inline-flex flex-col items-center gap-1">
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusStyles()} ${getUrgencyStyles(daysSinceUpdate)}`}>
        {isYellowStripe && <AlertTriangle size={12} className="mr-1" />}
        {status}
      </span>
      {isYellowStripe && daysSinceUpdate !== null && (
        <span className={`text-[10px] font-medium ${daysSinceUpdate >= 7 ? 'text-red-400' : daysSinceUpdate >= 4 ? 'text-orange-400' : 'text-yellow-400'}`}>
          {daysSinceUpdate === 0 ? 'Today' : daysSinceUpdate === 1 ? '1 day' : `${daysSinceUpdate} days`}
        </span>
      )}
    </div>
  )
}

export default StatusBadge
